<template>
  <v-card>
    <v-toolbar dark color="primary" tile flat class="mb-5">
      <v-btn icon dark @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $t('absence.title') }}</v-toolbar-title>
    </v-toolbar>

    <v-form ref="post_absence_form">
      <v-card-text class="py-3">
        <v-menu
          v-model="isDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          min-width="auto"
          location="bottom"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="dateRangeText"
              :label="$t('absence.period')"
              prepend-icon="mdi-calendar"
              :rules="[rules.required]"
              variant="outlined"
              density="compact"
              readonly
              v-bind="props"
              color="primary"
            ></v-text-field>
          </template>

          <!-- Vuetify 3ではピッカーとボタンをまとめるために v-card 等でラップします -->
          <v-card>
            <v-date-picker
              v-model="dates"
              hide-header
              multiple="range"
              color="primary"
              :locale="$i18n.locale"
              width="auto"
            ></v-date-picker>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" color="primary" @click="cancelDatePick">
                {{ $t('common.cancel') }}
              </v-btn>
              <!-- $refs.save は廃止されたため、単にメニューを閉じます (値はv-modelで即時反映されます) -->
              <v-btn variant="text" color="primary" @click="isDatePicker = false">
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <v-select
          v-model="reason"
          :rules="[rules.required]"
          :label="$t('absence.reason')"
          :items="choices"
          item-title="text"
          item-value="value"
          outlined
          dense
          color="primary"
        />

        <v-select
          v-model="infectionReason"
          :rules="[infectionRules]"
          v-if="reason === 'infection'"
          :label="$t('absence.detail')"
          :items="infectionChoices"
          item-title="text"
          item-value="value"
          outlined
          dense
          color="primary"
        />

        <v-textarea
          v-model="comment"
          v-if="reason === 'others'"
          :rules="[rules.counter500]"
          :label="$t('absence.comment')"
          auto-grow
          outlined
          dense
          color="primary"
        />
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close" color="error" text>
          {{ $t('common.cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click="clickPost" :loading="btnLoading" color="primary" variant="flat">
          {{ $t('absence.send') }}
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-form>
  </v-card>
</template>
<script lang="ts">
import Vue, { PropType, defineComponent } from 'vue';
import dayjs from 'dayjs';
import rules from '@/service/ValidRules';

import { chatModule } from '@/store/dataModules/chatModule';
import { AbsenceReason, AbsenceReasonType } from 'chaild-api/lib/index';

// model
import { VForm } from '@/model/Vuetify/VForm';
import LocalDataService from '@/service/LocalDataService';

interface DataType {
  dates: string[];
  startedAt: string;
  endedAt: string;
  reason: string;
  infectionReason: string;
  comment: string | null;
  fetchLoading: boolean;
  isDatePicker: boolean;
  btnLoading: boolean;
}

export default defineComponent({
  name: 'AbsenceModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    childId: {
      type: Number,
      default: NaN,
    },
    date: {
      type: Object as PropType<any>,
      default: {},
    },
    nurseryId: {
      type: Number,
      default: NaN,
    },
  },

  computed: {
    rules() {
      return rules;
    },
    form(): VForm {
      return (this.$refs as any).post_absence_form;
    },
    allowMinDate(): string {
      return dayjs().format('YYYY-MM-DD');
    },
    choices() {
      const t = (this as any).$t;
      return [
        { text: t('absenceReason.fever'), value: AbsenceReason.fever },
        { text: t('absenceReason.personal'), value: AbsenceReason.personal },
        { text: t('absence.infection'), value: 'infection' },
        { text: t('absence.othersWithComment'), value: AbsenceReason.others },
      ];
    },
    infectionChoices() {
      const t = (this as any).$t;
      return [
        { text: t('absenceReason.covid19'), value: AbsenceReason.covid19 },
        { text: t('absenceReason.infStrep'), value: AbsenceReason.infStrep },
        { text: t('absenceReason.infMycoplasma'), value: AbsenceReason.infMycoplasma },
        { text: t('absenceReason.infHFMD'), value: AbsenceReason.infHFMD },
        { text: t('absenceReason.infSlappedCheek'), value: AbsenceReason.infSlappedCheek },
        { text: t('absenceReason.infStomach'), value: AbsenceReason.infStomach },
        { text: t('absenceReason.infHerpangina'), value: AbsenceReason.infHerpangina },
        { text: t('absenceReason.infRS'), value: AbsenceReason.infRS },
        { text: t('absenceReason.infZoster'), value: AbsenceReason.infZoster },
        { text: t('absenceReason.infRoseola'), value: AbsenceReason.infRoseola },
        { text: t('absenceReason.infImpetigo'), value: AbsenceReason.infImpetigo },
      ];
    },
    dateRangeText() {
      if (!this.dates || this.dates.length === 0) {
        console.log(`dates: ${this.dates}`)
        return '';
      }
      // v-date-picker からの値を必ず YYYY-MM-DD で表示する
      console.log(`dates: ${this.dates}`)
      const rangeFirstDate = this.dates[0]
      const rangeLastDate = this.dates.slice(-1)[0]; 
      const rangeDates = [rangeFirstDate,rangeLastDate]
      // UI 表示用は 日-月-年 形式に変更 (バックエンドへ送る値は従来どおり YYYY-MM-DD を使用)
      const formatted = rangeDates.map((d: string) => dayjs(d).format('DD-MM-YYYY'));
      return formatted.join(' ~ ');
    },
    infectionRules() {
      if (this.reason === 'infection' && !this.infectionReason) {
        return (this as any).$t('absence.required');
      } else {
        return true;
      }
    },
  },

  watch: {
    isOpen: {
      async handler(val: boolean) {
        if (val) {
          this.dates = [];
          this.startedAt = '';
          this.endedAt = '';
          this.reason = '';
          this.comment = null;
        }
      },
      deep: true,
    },
  },

  data: (): DataType => ({
    dates: [],
    startedAt: '',
    endedAt: '',
    reason: '',
    infectionReason: '',
    comment: '',
    fetchLoading: false,
    isDatePicker: false,
    btnLoading: false,
  }),

  methods: {
    close() {
      this.$emit('close');
    },
    cancelDatePick() {
      this.dateRangeText = '';
      this.dates = [];
      this.isDatePicker = false;
    },
    async clickPost() {
      if (this.form.validate()) {
        if (this.dates.length === 1) {
          // 1日だけ選択された場合も YYYY-MM-DD で統一
          this.startedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
          this.endedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
          console.log('1日だけ選択された場合', this.startedAt, this.endedAt);
        } else {
          const rangeFirstDate = this.dates[0]
          const rangeLastDate = this.dates.slice(-1)[0]; 
          this.dates = [rangeFirstDate,rangeLastDate]
          const d1 = dayjs(this.dates[0]);
          const d2 = dayjs(this.dates[1]);
          if (d1.isAfter(d2)) {
            this.startedAt = dayjs(this.dates[1]).format('YYYY-MM-DD');
            this.endedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
            console.log('d1.isAfter(d2) の場合', this.startedAt, this.endedAt);
          } else {
            this.startedAt = dayjs(this.dates[0]).format('YYYY-MM-DD');
            this.endedAt = dayjs(this.dates[1]).format('YYYY-MM-DD');
            console.log('d1.isBefore(d2) の場合', this.startedAt, this.endedAt);
          }
        }
        this.btnLoading = true;
        if (!this.comment) {
          this.comment = null;
        }
        try {
          if (this.reason === 'infection') {
            await chatModule.postChatsAbsence({
              childId: this.childId,
              startedAt: this.startedAt,
              endedAt: this.endedAt,
              reason: this.infectionReason,
            });
          } else {
            await chatModule.postChatsAbsence({
              childId: this.childId,
              startedAt: this.startedAt,
              endedAt: this.endedAt,
              reason: this.reason,
              comment: this.comment,
            });
          }

          this.$emit('posted');
          this.$emit('close');
          this.btnLoading = false;
        } catch (error) {
          this.$emit('close');
          this.btnLoading = false;
        }
      }
    },
  },

  async mounted() {
    this.fetchLoading = true;
    await chatModule.getChatsAbsence(this.childId),
      this.$nextTick(() => {
        this.fetchLoading = false;
      });
  },
});
</script>
<style lang="scss" scoped></style>
